import { UIDtoFactory } from '@project44-models/iam-gateway';
import { UiDtoFactoryEnum } from '@project44-models/iam-gateway/dist/enums';

import { IUnifiedRolesDomain } from '../../../UnifiedRoles';
import { TenantSpecificTenantAdminRolesEnum } from '../../../UnifiedRoles/domain/enums';
import {
  IUserTenantSpecificRoles,
  IUserTenantSpecificRolesConstructor,
} from '../interfaces';

export class UserTenantSpecificRoles implements IUserTenantSpecificRoles {
  public unifiedRolesEditorFallback: IUnifiedRolesDomain.IRolesEditorPolicy;

  private constructor(
    private readonly payload: IUserTenantSpecificRolesConstructor,
  ) {
    this.unifiedRolesEditorFallback =
      this.payload.createUnifiedRolesEditorPolicy({});
  }

  public readonly getUserTenantSpecificRoles =
    async (): Promise<IUnifiedRolesDomain.IRolesEditorPolicy> => {
      const { userTenantSpecificUnifiedRoles, tenantUnifiedRoles } =
        await this.composeUserTenantSpecificUnifiedRolesPayload();

      return this.payload.createUnifiedRolesEditorPolicy({
        unifiedRolesAssignedToResource: userTenantSpecificUnifiedRoles,
        allAvailableUnifiedRoles: UIDtoFactory.create(
          UiDtoFactoryEnum.UNIFIED_ROLE_COLLECTION,
          {
            roles: [
              ...userTenantSpecificUnifiedRoles.toSerializedModel().roles,
              ...tenantUnifiedRoles.toSerializedModel().roles,
            ],
          },
        ),
      });
    };

  public readonly updateUserTenantSpecificRoles = async (
    updatedUserAssignedUnifiedRoles: IUnifiedRolesDomain.TUnifiedRoleCollectionDto,
  ): Promise<void> => {
    await this.payload.userTenantSpecificUnifiedRolesUpdateCommand.execute(
      this.payload.userPrincipal,
      {
        tenant: this.payload.tenant,
        user: this.payload.user,
        unifiedRoles: updatedUserAssignedUnifiedRoles,
      },
    );
  };

  public readonly changeUserTenantSpecificAdminRolesType = async (payload: {
    tenantSpecificTenantAdminPolicy: TenantSpecificTenantAdminRolesEnum;
    featureFlags: Record<string | symbol | number, boolean>;
  }): Promise<IUnifiedRolesDomain.TUnifiedRolesMap> => {
    const { featureFlags, tenantSpecificTenantAdminPolicy } = payload;

    await this.payload.changeUserTenantSpecificAdminRolesType.execute(
      this.payload.userPrincipal,
      {
        featureFlags,
        userTenantSpecificRolesHandler: this,
        tenantSpecificTenantAdminPolicy,
      },
    );

    const userTenantSpecificRolesPolicy =
      await this.getUserTenantSpecificRoles();

    return userTenantSpecificRolesPolicy.composeResourceTenantSpecificRolesMap(
      payload?.featureFlags,
    );
  };

  public static create(
    payload: IUserTenantSpecificRolesConstructor,
  ): UserTenantSpecificRoles {
    return new UserTenantSpecificRoles(payload);
  }

  private readonly composeUserTenantSpecificUnifiedRolesPayload =
    async (): Promise<{
      userTenantSpecificUnifiedRoles: IUnifiedRolesDomain.TUnifiedRoleCollectionDto;
      tenantUnifiedRoles: IUnifiedRolesDomain.TUnifiedRoleCollectionDto;
    }> => {
      const [{ userTenantSpecificUnifiedRoles }, { tenantUnifiedRoles }] =
        await Promise.all([
          this.payload.userTenantSpecificUnifiedRolesQuery.execute(
            this.payload.userPrincipal,
            {
              tenant: this.payload.tenant,
              user: this.payload.user,
            },
          ),
          this.payload.tenantUnifiedRolesQuery.execute(
            this.payload.userPrincipal,
            {
              tenant: this.payload.tenant,
            },
          ),
        ]);

      return {
        userTenantSpecificUnifiedRoles,
        tenantUnifiedRoles,
      };
    };
}
